<template>
  <header :class="['nav', { 'nav--solid': scrolled }]">
    <div class="ec-container nav__inner">
      <a href="#home" class="brand" @click="closeMenu">
        <span class="brand__mark" aria-hidden="true">
          <svg viewBox="0 0 32 32" width="30" height="30">
            <defs>
              <linearGradient id="brandGrad" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0" stop-color="#FF9900" />
                <stop offset="0.5" stop-color="#FFB84D" />
                <stop offset="1" stop-color="#FF6F00" />
              </linearGradient>
            </defs>
            <path fill="url(#brandGrad)" d="M23 13.2A8 8 0 0 0 7.6 12 6 6 0 0 0 8 24h14.5a5.5 5.5 0 0 0 .5-10.98Z" opacity="0.95"/>
            <circle cx="16" cy="16" r="3" fill="#04101f"/>
          </svg>
        </span>
        <span class="brand__text">
          <strong>Easycloud</strong>
          <em>{{ $t('nav.brandSub') }}</em>
        </span>
      </a>

      <nav :class="['nav__links', { 'is-open': menuOpen }]">
        <a v-for="item in links" :key="item.href" :href="item.href" @click="closeMenu">{{ item.label }}</a>
        <a href="#contact" class="nav__cta-mobile ec-btn ec-btn-primary" @click="closeMenu">{{ $t('nav.ctaMobile') }}</a>
      </nav>

      <div class="nav__actions">
        <!-- Language switcher -->
        <div class="lang" :class="{ open: langOpen }" v-click-outside="closeLang">
          <button class="lang__btn" :aria-expanded="langOpen" :aria-label="$t('nav.language')" @click="langOpen = !langOpen">
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true">
              <circle cx="12" cy="12" r="9" /><path d="M3 12h18" /><path d="M12 3a14 14 0 0 1 0 18 14 14 0 0 1 0-18Z" />
            </svg>
            <span class="lang__cur">{{ currentLocale.short }}</span>
            <span class="lang__chev" :class="{ open: langOpen }">▾</span>
          </button>
          <ul class="lang__menu" role="menu">
            <li v-for="l in locales" :key="l.code">
              <button
                role="menuitem"
                :class="{ active: l.code === $locale }"
                @click="pickLocale(l.code)"
              >{{ l.label }}<span v-if="l.code === $locale">✓</span></button>
            </li>
          </ul>
        </div>

        <a href="#contact" class="ec-btn ec-btn-primary nav__cta">{{ $t('nav.cta') }}</a>
        <button class="nav__burger" :aria-expanded="menuOpen" :aria-label="$t('nav.menu')" @click="menuOpen = !menuOpen">
          <span :class="{ open: menuOpen }"></span>
        </button>
      </div>
    </div>
  </header>
</template>

<script>
import { availableLocales, setLocale } from '../i18n'

export default {
  name: 'TheHeader',
  // local directive: close the language menu when clicking elsewhere
  directives: {
    clickOutside: {
      mounted(el, binding) {
        el._onDocClick = (e) => { if (!el.contains(e.target)) binding.value(e) }
        document.addEventListener('click', el._onDocClick)
      },
      unmounted(el) { document.removeEventListener('click', el._onDocClick) }
    }
  },
  data() {
    return {
      scrolled: false,
      menuOpen: false,
      langOpen: false,
      locales: availableLocales,
      navKeys: [
        { key: 'products', href: '#products' },
        { key: 'solutions', href: '#solutions' },
        { key: 'why', href: '#why' },
        { key: 'about', href: '#about' },
        { key: 'news', href: '#news' }
      ]
    }
  },
  computed: {
    links() {
      return this.navKeys.map((n) => ({ href: n.href, label: this.$t(`nav.links.${n.key}`) }))
    },
    currentLocale() {
      return this.locales.find((l) => l.code === this.$locale) || this.locales[0]
    }
  },
  mounted() {
    this.onScroll()
    window.addEventListener('scroll', this.onScroll, { passive: true })
  },
  beforeUnmount() {
    window.removeEventListener('scroll', this.onScroll)
  },
  methods: {
    onScroll() { this.scrolled = window.scrollY > 24 },
    closeMenu() { this.menuOpen = false },
    closeLang() { this.langOpen = false },
    pickLocale(code) { setLocale(code); this.langOpen = false }
  }
}
</script>

<style scoped>
.nav {
  position: fixed; top: 0; left: 0; right: 0; z-index: 100;
  transition: background .35s ease, border-color .35s ease, backdrop-filter .35s ease, box-shadow .35s ease;
  border-bottom: 1px solid transparent;
}
.nav--solid {
  background: rgba(6, 9, 20, 0.78);
  backdrop-filter: blur(18px) saturate(140%);
  border-bottom-color: var(--ec-line);
  box-shadow: 0 10px 40px -28px rgba(0, 0, 0, 0.9);
}
.nav__inner { display: flex; align-items: center; justify-content: space-between; height: 74px; }

.brand { display: flex; align-items: center; gap: 11px; }
.brand__mark { display: grid; place-items: center; filter: drop-shadow(0 4px 14px rgba(255,153,0,.4)); }
.brand__text { display: flex; flex-direction: column; line-height: 1.05; }
.brand__text strong { font-size: 19px; font-weight: 800; letter-spacing: -0.01em; }
.brand__text em { font-style: normal; font-size: 11px; color: var(--ec-text-mute); letter-spacing: 0.32em; }

.nav__links { display: flex; align-items: center; gap: 34px; }
.nav__links > a { font-size: 15px; font-weight: 500; color: var(--ec-text-soft); position: relative; transition: color .2s ease; }
.nav__links > a:hover { color: var(--ec-text); }
.nav__links > a::after {
  content: ""; position: absolute; left: 0; right: 100%; bottom: -6px; height: 2px;
  background: var(--ec-grad); border-radius: 2px; transition: right .28s ease;
}
.nav__links > a:hover::after { right: 0; }
.nav__cta-mobile { display: none; }

.nav__actions { display: flex; align-items: center; gap: 14px; }
.nav__cta { padding: 11px 22px; font-size: 14px; }

/* Language switcher */
.lang { position: relative; }
.lang__btn {
  display: inline-flex; align-items: center; gap: 7px;
  padding: 8px 12px; border-radius: 999px;
  background: rgba(255,255,255,.05); border: 1px solid var(--ec-line);
  color: var(--ec-text-soft); font-size: 13px; font-weight: 600;
  transition: color .2s ease, border-color .2s ease, background .2s ease;
}
.lang__btn:hover { color: var(--ec-text); border-color: rgba(255,153,0,.4); }
.lang__cur { letter-spacing: .02em; }
.lang__chev { font-size: 10px; transition: transform .25s ease; }
.lang__chev.open { transform: rotate(180deg); }
.lang__menu {
  position: absolute; top: calc(100% + 10px); right: 0; min-width: 150px;
  list-style: none; margin: 0; padding: 6px;
  background: rgba(10,14,28,.97); border: 1px solid var(--ec-line); border-radius: 14px;
  box-shadow: var(--ec-shadow); backdrop-filter: blur(18px);
  opacity: 0; transform: translateY(-8px); pointer-events: none;
  transition: opacity .22s ease, transform .22s ease;
}
.lang.open .lang__menu { opacity: 1; transform: none; pointer-events: auto; }
.lang__menu li { display: block; }
.lang__menu button {
  width: 100%; display: flex; align-items: center; justify-content: space-between;
  padding: 10px 12px; border-radius: 9px; border: none; background: transparent;
  color: var(--ec-text-soft); font-size: 14px; font-weight: 500; text-align: left;
  transition: background .18s ease, color .18s ease;
}
.lang__menu button:hover { background: rgba(255,255,255,.06); color: var(--ec-text); }
.lang__menu button.active { color: var(--ec-cyan); }
.lang__menu button span { color: var(--ec-cyan); font-weight: 700; }

.nav__burger { display: none; width: 42px; height: 42px; border: 1px solid var(--ec-line); border-radius: 12px; background: rgba(255,255,255,.04); position: relative; }
.nav__burger span, .nav__burger span::before, .nav__burger span::after {
  content: ""; position: absolute; left: 50%; transform: translateX(-50%); width: 18px; height: 2px; background: var(--ec-text); border-radius: 2px; transition: .3s ease;
}
.nav__burger span { top: 50%; margin-top: -1px; }
.nav__burger span::before { top: -6px; }
.nav__burger span::after { top: 6px; }
.nav__burger span.open { background: transparent; }
.nav__burger span.open::before { top: 0; transform: translateX(-50%) rotate(45deg); }
.nav__burger span.open::after { top: 0; transform: translateX(-50%) rotate(-45deg); }

@media (max-width: 900px) {
  .nav__cta { display: none; }
  .nav__burger { display: block; }
  .nav__links {
    position: fixed; inset: 74px 0 auto 0;
    flex-direction: column; gap: 4px; align-items: stretch;
    padding: 18px 24px 28px;
    background: rgba(8, 12, 26, 0.97); backdrop-filter: blur(20px);
    border-bottom: 1px solid var(--ec-line);
    transform: translateY(-130%); opacity: 0; pointer-events: none;
    transition: transform .4s cubic-bezier(.16,.84,.44,1), opacity .3s ease;
  }
  .nav__links.is-open { transform: none; opacity: 1; pointer-events: auto; }
  .nav__links > a { padding: 14px 6px; font-size: 17px; border-bottom: 1px solid var(--ec-line); }
  .nav__links > a::after { display: none; }
  .nav__cta-mobile { display: inline-flex; margin-top: 16px; border: none; }
}
</style>
